import { useEffect, useState } from "react";
import { Users, IndianRupee, Percent, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { appApi, inr, inrPaise, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const TABS = [
  { k: "subs", l: "Sub-merchants", icon: Users },
  { k: "earnings", l: "Earnings", icon: IndianRupee },
  { k: "markups", l: "Markups", icon: Percent },
];

export default function Reseller() {
  const [tab, setTab] = useState("subs");
  const [subs, setSubs] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [markups, setMarkups] = useState(null);
  const [denied, setDenied] = useState("");
  const [openSub, setOpenSub] = useState("");
  const [subDetail, setSubDetail] = useState({});
  const [defaultPct, setDefaultPct] = useState("");
  const [subPct, setSubPct] = useState({});
  const [busy, setBusy] = useState(false);

  const guard = (e) => {
    const msg = formatApiErrorDetail(e.response?.data?.detail);
    if (e.response?.status === 403 || /reseller/i.test(msg)) setDenied(msg);
    else toast.error(msg);
  };

  const loadSubs = () => appApi.get("reseller/subs").then((d) => setSubs(d.subs || [])).catch(guard);
  const loadEarnings = () => appApi.get("reseller/earnings", { limit: 100 }).then(setEarnings).catch(guard);
  const loadMarkups = () =>
    appApi.get("reseller/markups").then((d) => {
      setMarkups(d);
      setDefaultPct(d.default?.markup_pct ?? "");
    }).catch(guard);

  useEffect(() => { loadSubs(); loadEarnings(); loadMarkups(); /* eslint-disable-next-line */ }, []);

  const toggleSub = (s) => {
    const id = s.id || s.user_id;
    if (openSub === id) { setOpenSub(""); return; }
    setOpenSub(id);
    setSubDetail((d) => ({ ...d, [id]: null }));
    Promise.allSettled([
      appApi.get(`reseller/subs/${id}/orders`, { limit: 50 }),
      appApi.get(`reseller/subs/${id}/wallet`),
    ]).then(([o, w]) => setSubDetail((d) => ({
      ...d,
      [id]: {
        orders: o.status === "fulfilled" ? o.value.orders || [] : [],
        wallet: w.status === "fulfilled" ? w.value : null,
      },
    })));
  };

  const saveDefault = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await appApi.put("reseller/markups/default", { markup_pct: parseFloat(defaultPct) || 0 });
      toast.success("Default markup saved.");
      loadMarkups();
    } catch (err) { guard(err); } finally { setBusy(false); }
  };

  const saveSubMarkup = async (id) => {
    setBusy(true);
    try {
      await appApi.put(`reseller/markups/${id}`, { markup_pct: parseFloat(subPct[id]) || 0 });
      toast.success("Sub-merchant markup saved.");
      loadMarkups();
    } catch (err) { guard(err); } finally { setBusy(false); }
  };

  const clearSubMarkup = async (id) => {
    setBusy(true);
    try {
      await appApi.del(`reseller/markups/${id}`);
      toast.success("Override cleared — default markup applies.");
      loadMarkups();
    } catch (err) { guard(err); } finally { setBusy(false); }
  };

  if (denied)
    return (
      <div data-testid="reseller-denied" className="max-w-2xl border-2 border-obsidian bg-white p-8">
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Reseller Program</p>
        <h1 className="font-display font-black tracking-tight text-2xl mt-1">Not enabled on this account</h1>
        <p className="text-sm text-zinc-500 mt-3">{denied}. Ask ShipZilla admin to enable the reseller program for you.</p>
      </div>
    );

  return (
    <div data-testid="reseller-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Reseller Program</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">My Reseller Desk</h1>
        <p className="text-sm text-zinc-500 mt-2">Your linked sub-merchants, the margin you earn on their bookings, and your markup controls.</p>
      </div>

      <div className="flex gap-0 border-2 border-obsidian w-fit bg-white flex-wrap" data-testid="reseller-tabs">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`reseller-tab-${t.k}`}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs font-mono font-bold uppercase tracking-widest transition-colors duration-150 ${tab === t.k ? "bg-obsidian text-sky-300" : "hover:bg-acid/40"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
      </div>

      {tab === "earnings" && (
        <div className="space-y-6" data-testid="reseller-earnings">
          <div className="grid grid-cols-2 border-2 border-obsidian bg-white max-w-2xl">
            <div className="p-6 border-r-2 border-obsidian" data-testid="reseller-earnings-total">
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Total margin earned</p>
              <p className="font-display font-black text-3xl tracking-tight mt-2">{inrPaise(earnings?.total_paise)}</p>
            </div>
            <div className="p-6" data-testid="reseller-earnings-mtd">
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">This month</p>
              <p className="font-display font-black text-3xl tracking-tight mt-2">{inrPaise(earnings?.mtd_paise)}</p>
            </div>
          </div>
          <div className="border-2 border-obsidian bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]" data-testid="reseller-earnings-table">
              <tbody>
                {(earnings?.earnings || []).map((e, i) => (
                  <tr key={e.id || i} className="border-b border-zinc-100 last:border-0">
                    <td className="px-5 py-3 font-mono text-xs font-bold">{e.awb || e.order_id || "—"}</td>
                    <td className="px-5 py-3 text-zinc-600 text-xs">{e.sub_name || e.sub_email || "—"}</td>
                    <td className="px-5 py-3 font-mono text-xs text-zinc-500">{fmtDateTime(e.created_at)}</td>
                    <td className="px-5 py-3 text-right font-mono text-xs font-bold text-green-700">+{inrPaise(e.amount_paise ?? e.margin_paise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {earnings && (earnings.earnings || []).length === 0 && (
              <p className="px-5 py-10 text-sm text-zinc-500" data-testid="reseller-earnings-empty">No earnings yet — margins appear here when your sub-merchants book shipments.</p>
            )}
          </div>
        </div>
      )}

      {tab === "subs" && (
        <div className="border-2 border-obsidian bg-white" data-testid="reseller-subs">
          {(subs || []).map((s) => {
            const id = s.id || s.user_id;
            const det = subDetail[id];
            return (
              <div key={id} className="border-b-2 border-obsidian last:border-0">
                <button onClick={() => toggleSub(s)} data-testid={`reseller-sub-${id}`}
                  className="w-full px-5 py-4 flex items-center justify-between gap-4 flex-wrap text-left hover:bg-acid/20 transition-colors duration-100">
                  <div>
                    <p className="font-bold">{s.name || "—"} <span className="text-zinc-400 font-normal">· {s.business || s.email}</span></p>
                    <p className="text-xs font-mono text-zinc-500 mt-0.5">{s.orders_count ?? 0} orders · joined {fmtDateTime(s.created_at)}</p>
                  </div>
                  {openSub === id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {openSub === id && (
                  <div className="px-5 py-4 bg-paper border-t-2 border-obsidian" data-testid={`reseller-sub-panel-${id}`}>
                    {!det && <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading…</p>}
                    {det && (
                      <div className="space-y-4">
                        {det.wallet && (
                          <p className="text-xs font-mono text-zinc-600">
                            Wallet balance: <span className="font-bold text-obsidian">{inr(det.wallet.balance_inr ?? det.wallet.balance)}</span>
                          </p>
                        )}
                        <table className="w-full text-sm">
                          <tbody>
                            {det.orders.map((o) => (
                              <tr key={o.id || o.awb} className="border-b border-zinc-200 last:border-0">
                                <td className="py-2.5 font-mono text-xs font-bold">{o.awb}</td>
                                <td className="py-2.5 text-zinc-600 text-xs">{o.courier_display_name || o.courier_name}</td>
                                <td className="py-2.5"><StatusBadge status={o.status} /></td>
                                <td className="py-2.5 text-right font-mono text-xs font-bold">{o.rate != null ? inr(o.rate) : "—"}</td>
                              </tr>
                            ))}
                            {det.orders.length === 0 && <tr><td className="py-6 text-sm text-zinc-500">No orders yet from this sub-merchant.</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {subs && subs.length === 0 && (
            <p className="px-5 py-10 text-sm text-zinc-500" data-testid="reseller-subs-empty">No sub-merchants linked yet — ask ShipZilla admin to link merchants to your reseller account.</p>
          )}
        </div>
      )}

      {tab === "markups" && (
        <div className="space-y-6 max-w-3xl" data-testid="reseller-markups">
          <form onSubmit={saveDefault} className="border-2 border-obsidian bg-white p-6">
            <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Default markup % on sub-merchant bookings</p>
            <p className="text-xs text-zinc-400 mt-1">Your margin = this % on top of the platform rate. Applied when no per-client override exists.</p>
            <div className="flex gap-3 mt-4 items-end">
              <input type="number" min="0" step="0.5" value={defaultPct} onChange={(e) => setDefaultPct(e.target.value)} required
                data-testid="reseller-default-markup-input"
                className="w-40 border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
              <button type="submit" disabled={busy} data-testid="reseller-default-markup-save"
                className="bg-obsidian text-sky-300 font-bold px-6 py-2.5 border-2 border-obsidian text-xs uppercase tracking-widest hover:translate-y-[2px] transition-all duration-150 disabled:opacity-60">
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </form>

          <div className="border-2 border-obsidian bg-white">
            <p className="px-5 py-4 border-b-2 border-obsidian text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Per-client overrides</p>
            {(markups?.overrides || []).map((o) => {
              const id = o.sub_user_id || o.user_id || o.id;
              return (
                <div key={id} className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap border-b border-zinc-100 last:border-0" data-testid={`reseller-override-${id}`}>
                  <div>
                    <p className="font-bold text-sm">{o.sub_name || o.name || o.sub_email || id}</p>
                    <p className="text-xs font-mono text-zinc-500">current: {o.markup_pct}%</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" step="0.5" placeholder="new %"
                      value={subPct[id] ?? ""} onChange={(e) => setSubPct((p) => ({ ...p, [id]: e.target.value }))}
                      data-testid={`reseller-override-input-${id}`}
                      className="w-24 border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none" />
                    <button onClick={() => saveSubMarkup(id)} disabled={busy} data-testid={`reseller-override-save-${id}`}
                      className="text-[11px] font-bold uppercase tracking-widest bg-acid text-white px-4 py-2 border-2 border-obsidian disabled:opacity-60">Set</button>
                    <button onClick={() => clearSubMarkup(id)} disabled={busy} aria-label="Clear override" data-testid={`reseller-override-clear-${id}`}
                      className="p-2 border-2 border-obsidian text-red-600 hover:bg-red-50 disabled:opacity-60"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              );
            })}
            {markups && (markups.overrides || []).length === 0 && (
              <p className="px-5 py-8 text-sm text-zinc-500" data-testid="reseller-overrides-empty">No overrides — the default markup applies to every sub-merchant.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
